/**
 * Selleasy Variant Swatches
 * Replaces Selleasy popup dropdown selectors with visual thumbnail swatches
 * and rebuilds popup carousel to show only selected variant images.
 *
 * ONLY targets the popup (lb-popup). The inline "People also bought"
 * bundle widget keeps its original dropdown.
 *
 * Selleasy carousel structure:
 *   .lb-product-info-img-carousel (container)
 *     .lb-product-info-img-carousel-item (scroll-snap wrapper)
 *       .lb-product-info-img-carousel-item-img[image-index="N"] (each slide)
 *         lb-card-image > .lb-card-image-cont > img.lb-card-img[alt="VariantName"]
 */
(function () {
  'use strict';

  const PRODUCT_CACHE = {};
  const PROCESSED_ATTR = 'data-swatches-applied';
  const IN_FLIGHT = new WeakSet();

  async function fetchProduct(handle) {
    if (PRODUCT_CACHE[handle]) return PRODUCT_CACHE[handle];
    try {
      const res = await fetch(`${window.Shopify.routes.root}products/${handle}.json`);
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      PRODUCT_CACHE[handle] = data.product;
      return data.product;
    } catch (e) {
      console.warn('[selleasy-swatches] Failed to fetch product:', handle, e);
      return null;
    }
  }

  function getVariantThumbnails(product) {
    const thumbnails = {};
    if (!product || !product.variants) return thumbnails;

    for (const variant of product.variants) {
      const colorOption = variant.option1;
      if (colorOption && !thumbnails[colorOption] && variant.featured_image) {
        thumbnails[colorOption] = variant.featured_image.src;
      }
    }

    // Fallback: match by image alt text
    if (product.images) {
      for (const variant of product.variants) {
        const colorOption = variant.option1;
        if (colorOption && !thumbnails[colorOption]) {
          const matchingImg = product.images.find(img =>
            img.alt && img.alt.toLowerCase().includes(colorOption.toLowerCase())
          );
          if (matchingImg) thumbnails[colorOption] = matchingImg.src;
        }
      }
      // Fallback: match by image_id
      for (const variant of product.variants) {
        const colorOption = variant.option1;
        if (colorOption && !thumbnails[colorOption] && variant.image_id) {
          const img = product.images.find(i => i.id === variant.image_id);
          if (img) thumbnails[colorOption] = img.src;
        }
      }
    }

    return thumbnails;
  }

  function resizeImage(src, size) {
    if (!src || !src.includes('cdn.shopify.com')) return src;
    try {
      const url = new URL(src);
      url.searchParams.set('width', size);
      url.searchParams.set('height', size);
      url.searchParams.set('crop', 'center');
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  function buildSwatches(selectEl, thumbnails, product) {
    const container = document.createElement('div');
    container.className = 'lb-swatches-container';

    Array.from(selectEl.options).forEach((opt, index) => {
      const value = opt.text.trim();
      const swatch = document.createElement('button');
      swatch.className = 'lb-swatch';
      swatch.setAttribute('data-value', value);
      swatch.setAttribute('data-index', index);
      swatch.setAttribute('type', 'button');
      swatch.setAttribute('aria-label', value);
      swatch.title = value;

      if (opt.selected) swatch.classList.add('lb-swatch--active');

      const imgSrc = thumbnails[value];
      if (imgSrc) {
        const img = document.createElement('img');
        img.src = resizeImage(imgSrc, 80);
        img.alt = value;
        img.loading = 'lazy';
        swatch.appendChild(img);
      } else {
        const span = document.createElement('span');
        span.className = 'lb-swatch__text';
        span.textContent = value.substring(0, 2).toUpperCase();
        swatch.appendChild(span);
      }

      swatch.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        container.querySelectorAll('.lb-swatch').forEach(s => s.classList.remove('lb-swatch--active'));
        swatch.classList.add('lb-swatch--active');

        selectEl.selectedIndex = index;
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));

        rebuildCarousel(selectEl, value);
      });

      container.appendChild(swatch);
    });

    return container;
  }

  /**
   * Swap the swatch row in, replacing any row from a previous render so a
   * rebuild can't stack duplicates. Hides the vendor select it stands in for.
   */
  function renderSwatches(pickerCont, selectEl, thumbnails, product) {
    if (!pickerCont) return;
    const existing = pickerCont.querySelector('.lb-swatches-container');
    if (existing) existing.remove();
    selectEl.style.display = 'none';
    pickerCont.appendChild(buildSwatches(selectEl, thumbnails, product));
  }

  /**
   * Rebuild the Selleasy carousel: remove non-matching slides, re-index, scroll to start.
   * Works with Selleasy's scroll-snap carousel (not Swiper).
   */
  function rebuildCarousel(selectEl, variantName) {
    const productInfo = selectEl.closest('lb-product-info');
    if (!productInfo) return;

    // Main carousel
    const scrollContainer = productInfo.querySelector('.lb-product-info-img-carousel-item');
    if (!scrollContainer) return;

    // Store original slides on first call
    if (!scrollContainer._originalSlides) {
      const slides = scrollContainer.querySelectorAll('.lb-product-info-img-carousel-item-img');
      scrollContainer._originalSlides = Array.from(slides).map(s => s.cloneNode(true));
    }

    const lowerVariant = variantName.toLowerCase();

    // Get product name to identify "general" images (Selleasy sets alt to product title when Shopify alt is null)
    const productName = productInfo ? (productInfo.querySelector('.lb-card-title-host')?.textContent || '').trim().toLowerCase() : '';

    // Filter: keep slides whose img alt matches the variant, has no alt, or alt equals the product name (general)
    const matching = scrollContainer._originalSlides.filter(slide => {
      const img = slide.querySelector('img');
      if (!img) return false;
      const hasAlt = img.hasAttribute('alt');
      const alt = (img.alt || '').trim().toLowerCase();
      // Show if: no alt attribute, empty alt, alt matches product name (general), or alt matches variant
      return !hasAlt || alt === '' || alt === productName || alt.includes(lowerVariant);
    });

    // If no variant-specific images found, show all (product has no alt-based filtering)
    const slidesToShow = matching.length > 0 ? matching : scrollContainer._originalSlides;

    // Remove all current slides
    const currentSlides = scrollContainer.querySelectorAll('.lb-product-info-img-carousel-item-img');
    currentSlides.forEach(s => s.remove());

    // Insert filtered slides with correct indices
    slidesToShow.forEach((slide, i) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('image-index', i);
      scrollContainer.appendChild(clone);
    });

    // Scroll back to the first slide
    scrollContainer.scrollTo({ left: 0, behavior: 'instant' });

    // Mark as ready (reveals from opacity: 0)
    scrollContainer.classList.add('lb-swatches-ready');

    // Also rebuild the bottom thumbnail strip
    rebuildThumbnails(productInfo, lowerVariant);

    // Sync thumbnail active state when user scrolls/swipes the main carousel
    attachScrollSync(scrollContainer, productInfo);
  }

  /**
   * Poll the main carousel to detect which slide is currently visible,
   * then update the active thumbnail indicator accordingly.
   * Polling is used because Selleasy's internal navigation (arrows, swipe)
   * doesn't reliably fire scroll events or DOM mutations we can observe.
   */
  function attachScrollSync(scrollContainer, productInfo) {
    // Clean up previous poll
    if (scrollContainer._syncInterval) {
      clearInterval(scrollContainer._syncInterval);
    }

    var lastActiveIndex = 0;

    scrollContainer._syncInterval = setInterval(function () {
      // Stop polling if popup was closed (element removed from DOM)
      if (!document.contains(scrollContainer)) {
        clearInterval(scrollContainer._syncInterval);
        return;
      }

      var slides = scrollContainer.querySelectorAll('.lb-product-info-img-carousel-item-img');
      if (!slides.length) return;

      var containerRect = scrollContainer.getBoundingClientRect();
      var containerCenter = containerRect.left + containerRect.width / 2;

      var closestIndex = 0;
      var closestDist = Infinity;
      slides.forEach(function (slide, i) {
        var rect = slide.getBoundingClientRect();
        var slideCenter = rect.left + rect.width / 2;
        var dist = Math.abs(slideCenter - containerCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closestIndex = i;
        }
      });

      // Only update DOM if index actually changed
      if (closestIndex !== lastActiveIndex) {
        lastActiveIndex = closestIndex;
        var thumbContainer = productInfo.querySelector('.lb-product-info-img-carousel-thumbs');
        if (!thumbContainer) return;
        var thumbs = thumbContainer.querySelectorAll('.lb-product-info-img-carousel-thumbs-item');
        thumbs.forEach(function (t, i) {
          t.classList.toggle('is-selected', i === closestIndex);
        });
      }
    }, 150);
  }

  /**
   * Rebuild the bottom thumbnail strip to match filtered slides.
   * Selleasy thumbnail structure:
   *   .lb-product-info-img-carousel-thumbs (scroll-snap container)
   *     .lb-product-info-img-carousel-thumbs-item[image-index="N"] (each thumb)
   *       lb-card-image > .lb-card-image-cont > img[alt="VariantName"]
   */
  function rebuildThumbnails(productInfo, lowerVariant) {
    const thumbContainer = productInfo.querySelector('.lb-product-info-img-carousel-thumbs');
    if (!thumbContainer) return;

    // Store originals on first call
    if (!thumbContainer._originalThumbs) {
      const thumbs = thumbContainer.querySelectorAll('.lb-product-info-img-carousel-thumbs-item');
      thumbContainer._originalThumbs = Array.from(thumbs).map(t => t.cloneNode(true));
    }

    // Get product name for general image detection
    const productName = productInfo.querySelector('.lb-card-title-host')?.textContent?.trim().toLowerCase() || '';

    // Filter thumbnails: match variant name, no/empty alt, or alt equals product name (general)
    const matching = thumbContainer._originalThumbs.filter(thumb => {
      const img = thumb.querySelector('img');
      if (!img) return false;
      const hasAlt = img.hasAttribute('alt');
      const alt = (img.alt || '').trim().toLowerCase();
      return !hasAlt || alt === '' || alt === productName || alt.includes(lowerVariant);
    });

    const toShow = matching.length > 0 ? matching : thumbContainer._originalThumbs;

    // Remove current thumbnails
    const currentThumbs = thumbContainer.querySelectorAll('.lb-product-info-img-carousel-thumbs-item');
    currentThumbs.forEach(t => t.remove());

    // Get the main carousel scroll container for click-to-navigate
    const scrollContainer = productInfo.querySelector('.lb-product-info-img-carousel-item');

    // Insert filtered thumbnails with correct indices and click handlers
    toShow.forEach((thumb, i) => {
      const clone = thumb.cloneNode(true);
      clone.setAttribute('image-index', i);
      clone.classList.remove('is-selected');
      if (i === 0) clone.classList.add('is-selected');

      // Add click handler: scroll main carousel to this slide + update selected state
      clone.addEventListener('click', function () {
        // Update selected state on all thumbnails
        thumbContainer.querySelectorAll('.lb-product-info-img-carousel-thumbs-item').forEach(t => {
          t.classList.remove('is-selected');
        });
        clone.classList.add('is-selected');

        // Scroll main carousel to the matching slide
        if (scrollContainer) {
          const targetSlide = scrollContainer.querySelector('[image-index="' + i + '"]');
          if (targetSlide) {
            targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
        }
      });

      thumbContainer.appendChild(clone);
    });

    // Scroll thumbnails to start
    thumbContainer.scrollTo({ left: 0, behavior: 'instant' });

    // Mark as ready (reveals from opacity: 0)
    thumbContainer.classList.add('lb-swatches-ready');
  }

  async function processVariantPicker(pickerEl) {
    if (pickerEl.getAttribute(PROCESSED_ATTR)) return;
    // A scan can fire again while the product fetch is still pending; the
    // PROCESSED_ATTR is only set once the swatches actually land, so guard
    // concurrent runs separately instead of marking the picker up front. A
    // failed fetch then leaves the picker eligible for the next retry rather
    // than poisoning it for the life of the popup.
    if (IN_FLIGHT.has(pickerEl)) return;

    // Only target pickers inside the popup
    const isInPopup = pickerEl.closest('lb-popup') || pickerEl.closest('.lb-popup');
    if (!isInPopup) return;

    const selectEl = pickerEl.querySelector('select.lb-variant-picker');
    if (!selectEl) return;
    // Selleasy only lists variants that are in stock, so a product down to its
    // last available color renders a single-option select (Active Strap: only
    // Minted is available). Bailing on that (options.length <= 1) skipped the
    // carousel filter too - the popup then showed all 10 product photos, every
    // color, as 10 dots. There is nothing to pick with one option, but there is
    // still everything to filter, so process it like any other picker: one
    // swatch, carousel trimmed to that color.
    if (!selectEl.options.length) return;

    const productInfo = pickerEl.closest('lb-product-info');
    const handle = productInfo ? productInfo.getAttribute('lb-product-handle') : null;
    if (!handle) return;

    IN_FLIGHT.add(pickerEl);
    let product;
    try {
      product = await fetchProduct(handle);
    } finally {
      IN_FLIGHT.delete(pickerEl);
    }
    if (!product) return;
    if (!document.contains(pickerEl)) return;
    if (pickerEl.getAttribute(PROCESSED_ATTR)) return;
    pickerEl.setAttribute(PROCESSED_ATTR, 'true');

    const thumbnails = getVariantThumbnails(product);
    const pickerCont = pickerEl.querySelector('.lb-variant-picker-cont');
    renderSwatches(pickerCont, selectEl, thumbnails, product);

    // Now that a single-option select is processed rather than skipped, a select
    // that Selleasy fills progressively would leave us showing a stale swatch
    // set (PROCESSED_ATTR blocks a re-scan). Mirror any later option change.
    if (pickerCont && !selectEl._optionsObserver) {
      selectEl._optionsObserver = new MutationObserver(function () {
        renderSwatches(pickerCont, selectEl, thumbnails, product);
        const opt = selectEl.options[selectEl.selectedIndex];
        if (opt) rebuildCarousel(selectEl, opt.text.trim());
      });
      selectEl._optionsObserver.observe(selectEl, { childList: true });
    }

    // Filter to currently selected variant on initial load. The carousel slides
    // mount on Selleasy's own schedule, so wait for them to exist rather than
    // firing at a fixed delay and silently filtering an empty carousel.
    const selectedOption = selectEl.options[selectEl.selectedIndex];
    if (selectedOption) {
      whenSlidesReady(productInfo, () => rebuildCarousel(selectEl, selectedOption.text.trim()));
    }
  }

  function whenSlidesReady(productInfo, cb) {
    var attempts = 0;
    (function poll() {
      if (!document.contains(productInfo)) return;
      var slides = productInfo.querySelectorAll('.lb-product-info-img-carousel-item-img');
      if (slides.length) return cb();
      if (++attempts > 30) return; // ~6s
      setTimeout(poll, 200);
    })();
  }

  function scanAndProcess() {
    document.querySelectorAll('lb-variant-picker:not([' + PROCESSED_ATTR + '])').forEach(processVariantPicker);
  }

  var scanTimer = null;
  function scheduleScan() {
    if (scanTimer) return;
    scanTimer = setTimeout(function () {
      scanTimer = null;
      scanAndProcess();
    }, 100);
  }

  /**
   * Selleasy mounts <lb-popup> first and renders <lb-variant-picker> inside it
   * later, once its own product fetch resolves. Scanning only on the lb-popup
   * insertion (plus a fixed delay) is a race: on products whose picker lands
   * after the delay the swatches never get built, so the raw <select> stays and
   * the carousel is never filtered down to the selected color. Scan on every
   * mutation batch instead, and keep retrying for a few seconds after a popup
   * appears so a slow picker is still caught. PROCESSED_ATTR makes repeat scans
   * cheap no-ops.
   */
  function observe() {
    const observer = new MutationObserver(function (mutations) {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'LB-POPUP' || (node.querySelector && node.querySelector('lb-popup'))) {
            startRetryWindow();
          }
        }
      }
      scheduleScan();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  var retryInterval = null;
  function startRetryWindow() {
    if (retryInterval) return;
    var attempts = 0;
    retryInterval = setInterval(function () {
      attempts++;
      scanAndProcess();
      // ~6s of cover, or stop early once the popup is gone.
      if (attempts >= 20 || !document.querySelector('lb-popup')) {
        clearInterval(retryInterval);
        retryInterval = null;
      }
    }, 300);
  }

  function injectStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .lb-swatches-container {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 6px;
      }
      .lb-swatch {
        width: 40px;
        height: 40px;
        border-radius: 6px;
        border: 2px solid transparent;
        padding: 0;
        cursor: pointer;
        overflow: hidden;
        background: #f5f5f5;
        transition: border-color 0.15s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .lb-swatch:hover {
        border-color: #999;
      }
      .lb-swatch--active {
        border-color: #341E29;
      }
      .lb-swatch img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .lb-swatch__text {
        font-size: 11px;
        font-weight: 600;
        color: #666;
        line-height: 1;
      }
      lb-variant-picker[data-swatches-applied] .lb-variant-picker-title {
        display: none;
      }

      /* Hide popup content until swatches are applied to prevent flash */
      lb-popup .lb-product-info-img-carousel-item,
      lb-popup .lb-product-info-img-carousel-thumbs,
      lb-popup lb-variant-picker:not([data-swatches-applied]) .lb-variant-picker-cont {
        opacity: 0;
        transition: opacity 0.15s ease;
      }
      lb-popup .lb-product-info-img-carousel-item.lb-swatches-ready,
      lb-popup .lb-product-info-img-carousel-thumbs.lb-swatches-ready,
      lb-popup lb-variant-picker[data-swatches-applied] .lb-variant-picker-cont {
        opacity: 1;
      }
    `;
    document.head.appendChild(style);
  }

  function init() {
    injectStyles();
    scanAndProcess();
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
